// ---------------------------------------------------------------------------
// Document Assembly
// ---------------------------------------------------------------------------
// Main entry point for the page-builder renderer. Produces a complete HTML
// document from RenderInput.

import type {
  RenderInput,
  RenderContext,
  RenderedPage,
  PuckComponent,
} from "./types.js";
import type { NavItem } from "../schema/index.js";
import { buildHead } from "./head.js";
import { buildCssVariables } from "./css-cascade.js";
import { getRenderer } from "./registry.js";
import { attr, text, url as urlEscape } from "./escape.js";
import { generateSessionScript } from "../funnel/session-script.js";
import { buildAnimationAttrs, ANIMATION_OBSERVER_SCRIPT } from "./animation.js";
import { generatePageAnalyticsScript } from "./page-analytics-script.js";
import {
  buildPublishedStateStyleTag,
  buildScopedClassName,
  extractBaseInlineStyles,
  serializeStyleMap,
} from "./block-styles.js";
import { maybeOptimizeImageTag } from "./image-optimizer.js";

const LOTTIE_CDN_URL = "https://cdnjs.cloudflare.com/ajax/libs/lottie-web/5.12.2/lottie.min.js";
const LOTTIE_INIT_SCRIPT = `<script>(function(){if(typeof window==="undefined"||!window.lottie)return;var nodes=document.querySelectorAll("[data-ease-lottie]");if(!nodes.length)return;nodes.forEach(function(node){if(!(node instanceof HTMLElement))return;var source=node.dataset.lottieSource;if(!source)return;var speed=Number(node.dataset.lottieSpeed||"1");var trigger=node.dataset.lottieTrigger||"none";var autoplay=node.dataset.lottieAutoplay==="yes"&&trigger==="none";var loop=node.dataset.lottieLoop==="yes";var animation=window.lottie.loadAnimation({container:node,renderer:"svg",loop:loop,autoplay:autoplay,path:source});animation.setSpeed(Number.isFinite(speed)?Math.max(.1,Math.min(5,speed)):1);if(trigger==="hover"){animation.stop();node.addEventListener("mouseenter",function(){animation.goToAndPlay(0,true);});node.addEventListener("mouseleave",function(){animation.stop();});return;}if(trigger==="click"){animation.stop();node.addEventListener("click",function(){if(animation.isPaused){animation.play();return;}animation.pause();});return;}if(trigger==="scroll-into-view"&&"IntersectionObserver"in window){animation.stop();var observer=new IntersectionObserver(function(entries){entries.forEach(function(entry){if(entry.isIntersecting){animation.play();return;}animation.stop();});},{threshold:.35});observer.observe(node);}});})();</script>`;

// ---------------------------------------------------------------------------
// Context builder
// ---------------------------------------------------------------------------

function buildRenderContext(input: RenderInput): RenderContext {
  const cssVariables = input.siteContext
    ? buildCssVariables(input.siteContext.globalTheme)
    : "";

  return {
    submissionEndpoint: input.submissionEndpoint,
    pageId: input.pageId,
    tenantId: input.tenantId,
    versionId: input.versionId,
    cssVariables,
    funnelStepId: input.funnelContext?.stepId,
    abExperimentId: input.abContext?.experimentId,
    abVariantId: input.abContext?.variantId,
    turnstileSiteKey: input.turnstileSiteKey ?? null,
  };
}

// ---------------------------------------------------------------------------
// Content detection
// ---------------------------------------------------------------------------

function hasNavigationBarComponent(content: PuckComponent[]): boolean {
  return content.some((c) => c.type === "navigation-bar");
}

function hasComponentAnimation(component: PuckComponent): boolean {
  return buildAnimationAttrs(component.props) !== null;
}

function contentHasAnimation(content: PuckComponent[] | undefined): boolean {
  return Array.isArray(content) && content.some(hasComponentAnimation);
}

function hasLottieComponent(content: PuckComponent[] | undefined): boolean {
  return Array.isArray(content) && content.some((component) => component.type === "lottie-player");
}

function renderLottiePlayer(props: Record<string, unknown>): string {
  const source = typeof props.source === "string" ? props.source.trim() : "";
  if (!source) {
    return "<!-- lottie-player missing source -->";
  }

  const autoplay = props.autoplay === "no" ? "no" : "yes";
  const loop = props.loop === "no" ? "no" : "yes";
  const speed = typeof props.speed === "number" ? props.speed : Number(props.speed ?? 1);
  const trigger = typeof props.trigger === "string" ? props.trigger : "none";
  const width = typeof props.width === "string" && props.width.trim() ? props.width : "100%";
  const height = typeof props.height === "string" && props.height.trim() ? props.height : "auto";
  const style = [
    `width:${width}`,
    height === "auto" ? "min-height:240px" : `height:${height}`,
  ].join(";");

  return `<section class="ease-section ease-lottie-player"><div class="ease-lottie-player__frame" data-ease-lottie data-lottie-source="${attr(source)}" data-lottie-autoplay="${attr(autoplay)}" data-lottie-loop="${attr(loop)}" data-lottie-speed="${attr(String(speed))}" data-lottie-trigger="${attr(trigger)}" style="${attr(style)}"></div></section>`;
}

// ---------------------------------------------------------------------------
// Navigation renderers
// ---------------------------------------------------------------------------

function renderNavItem(item: NavItem): string {
  const href = item.url ? ` href="${urlEscape(item.url)}"` : "";
  const target = item.openInNewTab
    ? ' target="_blank" rel="noopener noreferrer"'
    : "";
  const label = text(item.label);

  let html = `<li><a${href}${target}>${label}</a>`;

  if (item.children && item.children.length > 0) {
    const childItems = item.children.map(renderNavItem).join("\n");
    html += `\n<ul>\n${childItems}\n</ul>`;
  }

  html += "</li>";
  return html;
}

function renderSiteHeader(mainNav: NavItem[]): string {
  const items = mainNav.map(renderNavItem).join("\n");
  return `<header class="ease-site-header" role="banner" style="view-transition-name: ease-site-header;">
<nav aria-label="Main navigation">
<ul class="ease-nav-list">
${items}
</ul>
</nav>
</header>`;
}

function renderSiteFooter(footerNav: NavItem[]): string {
  const items = footerNav
    .map((item) => {
      const href = item.url ? ` href="${urlEscape(item.url)}"` : "";
      const target = item.openInNewTab
        ? ' target="_blank" rel="noopener noreferrer"'
        : "";
      return `<li><a${href}${target}>${text(item.label)}</a></li>`;
    })
    .join("\n");

  return `<footer class="ease-site-footer" role="contentinfo" style="view-transition-name: ease-site-footer;">
<nav aria-label="Footer navigation">
<ul class="ease-footer-list">
${items}
</ul>
</nav>
</footer>`;
}

// ---------------------------------------------------------------------------
// Component renderer
// ---------------------------------------------------------------------------

function renderComponent(
  component: PuckComponent,
  ctx: RenderContext,
): string {
  const renderer = getRenderer(component.type);
  if (component.type === "lottie-player") {
    return renderLottiePlayer(component.props);
  }
  if (!renderer) {
    // Sanitize type to prevent HTML comment breakout (defense-in-depth)
    const safeType = component.type.replace(/-->/g, "").replace(/[<>]/g, "");
    return `<!-- unknown component: ${safeType} -->`;
  }

  const html = renderer.render(component.props, ctx);
  const animAttrs = buildAnimationAttrs(component.props) ?? "";
  const componentId = typeof component.props.id === "string" ? component.props.id : "";
  const baseStyles = extractBaseInlineStyles(component.props);
  const customAttrs = extractCustomWrapperAttributes(component.props);
  const classNames = [
    ...(componentId ? [buildScopedClassName(componentId)] : []),
    ...customAttrs.classNames,
  ];
  const classAttr = classNames.length > 0
    ? ` class="${attr(classNames.join(" "))}"`
    : "";
  const styleAttr = Object.keys(baseStyles).length > 0
    ? ` style="${attr(serializeStyleMap(baseStyles))}"`
    : "";
  const dataAttr = componentId ? ` data-block-id="${attr(componentId)}"` : "";
  const idAttr = customAttrs.elementId ? ` id="${attr(customAttrs.elementId)}"` : "";
  const customAttrString = customAttrs.customAttributes
    .map(({ name, value }) => ` ${name}="${attr(value)}"`)
    .join("");

  if (!animAttrs && !classAttr && !styleAttr && !dataAttr && !idAttr && !customAttrString) return html;

  return `<div${idAttr}${classAttr}${styleAttr}${dataAttr}${customAttrString}${animAttrs}>${html}</div>`;
}

function extractCustomWrapperAttributes(props: Record<string, unknown>): {
  elementId: string;
  classNames: string[];
  customAttributes: Array<{ name: string; value: string }>;
} {
  const dssStyles = typeof props._dssStyles === "object" && props._dssStyles !== null
    ? props._dssStyles as Record<string, unknown>
    : null;

  const elementId = typeof dssStyles?.elementId === "string"
    ? sanitizeElementId(dssStyles.elementId)
    : "";
  const classNames = Array.isArray(dssStyles?.classNames)
    ? sanitizeClassNames(
        dssStyles.classNames.filter((value): value is string => typeof value === "string"),
      )
    : [];
  const customAttributes = Array.isArray(dssStyles?.customAttributes)
    ? sanitizeCustomAttributes(
        dssStyles.customAttributes.filter(
          (
            value,
          ): value is { name?: unknown; value?: unknown } => typeof value === "object" && value !== null,
        ),
      )
    : [];

  return { elementId, classNames, customAttributes };
}

function sanitizeElementId(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "";
  return /^[a-zA-Z][a-zA-Z0-9_-]{0,63}$/.test(trimmed) ? trimmed : "";
}

function sanitizeClassNames(value: string[]): string[] {
  const seen = new Set<string>();
  const sanitized: string[] = [];

  for (const className of value) {
    const trimmed = className.trim();
    if (!/^[a-zA-Z_-][a-zA-Z0-9_-]{0,63}$/.test(trimmed) || seen.has(trimmed)) {
      continue;
    }
    seen.add(trimmed);
    sanitized.push(trimmed);
    if (sanitized.length >= 50) break;
  }

  return sanitized;
}

function sanitizeCustomAttributes(
  value: Array<{ name?: unknown; value?: unknown }>,
): Array<{ name: string; value: string }> {
  const seen = new Set<string>();
  const sanitized: Array<{ name: string; value: string }> = [];

  for (const attribute of value) {
    const name = typeof attribute.name === "string" ? attribute.name.trim().toLowerCase() : "";
    const attrValue = typeof attribute.value === "string"
      ? attribute.value.replace(/[\u0000-\u001F\u007F]/g, "").trim().replace(/\s+/g, " ").slice(0, 256)
      : "";

    if (!/^(data-[a-z0-9_-]{1,64}|aria-[a-z0-9_-]{1,64}|role)$/.test(name) || !attrValue || seen.has(name)) {
      continue;
    }
    if (name === "role" && !/^[a-z0-9_-]{1,64}$/.test(attrValue)) {
      continue;
    }

    seen.add(name);
    sanitized.push({ name, value: attrValue });
    if (sanitized.length >= 20) break;
  }

  return sanitized;
}

// ---------------------------------------------------------------------------
// Global header/footer helpers
// ---------------------------------------------------------------------------

/** Check if a Puck JSON record contains renderable component content */
function hasComponentContent(data: Record<string, unknown>): boolean {
  const content = data.content;
  return Array.isArray(content) && content.length > 0;
}

/** Extract PuckComponent[] from a Puck JSON record */
function extractComponents(data: Record<string, unknown>): PuckComponent[] {
  const content = data.content;
  if (!Array.isArray(content)) return [];
  return content as PuckComponent[];
}

function hasImgAttr(tag: string, name: string): boolean {
  return new RegExp(`\\b${name}\\s*=`, "i").test(tag);
}

function setImgAttr(tag: string, name: string, value: string): string {
  const pattern = new RegExp(`\\b${name}\\s*=\\s*["'][^"']*["']`, "i");
  if (pattern.test(tag)) {
    return tag.replace(pattern, `${name}="${value}"`);
  }
  return tag.replace(/^<img\b/i, `<img ${name}="${value}"`);
}

function isTrackingPixelTag(tag: string): boolean {
  return /facebook\.com\/tr\?/i.test(tag)
    || /\bwidth\s*=\s*["']?1["']?/i.test(tag) && /\bheight\s*=\s*["']?1["']?/i.test(tag)
    || /\bstyle\s*=\s*["'][^"']*display\s*:\s*none/i.test(tag);
}

function normalizeMediaTag(tag: string, imageIndex: number): string {
  let next = tag;

  if (isTrackingPixelTag(next)) {
    return next;
  }

  if (!hasImgAttr(next, "decoding")) {
    next = setImgAttr(next, "decoding", "async");
  }

  const isFirstImage = imageIndex === 0;
  next = setImgAttr(next, "loading", isFirstImage ? "eager" : "lazy");

  if (!hasImgAttr(next, "fetchpriority")) {
    next = setImgAttr(next, "fetchpriority", isFirstImage ? "high" : "low");
  }

  return next;
}

function optimizeRenderedHtml(html: string): string {
  let imageIndex = 0;
  return html.replace(/<img\b[^>]*>/gi, (tag) => {
    if (isTrackingPixelTag(tag)) {
      return normalizeMediaTag(tag, imageIndex);
    }

    const normalized = normalizeMediaTag(tag, imageIndex);
    imageIndex += 1;
    return maybeOptimizeImageTag(normalized);
  });
}

const SCROLL_PROGRESS_SCRIPT = `<script>(function(){var bar=document.querySelector('[data-ease-scroll-progress]');if(!(bar instanceof HTMLElement))return;var doc=document.documentElement;function update(){var max=doc.scrollHeight-doc.clientHeight;var pct=max>0?(doc.scrollTop/max)*100:0;bar.style.width=Math.max(0,Math.min(100,pct))+'%';}window.addEventListener('scroll',update,{passive:true});window.addEventListener('resize',update,{passive:true});update();})();</script>`;
const BACK_TO_TOP_SCRIPT = `<script>(function(){var btn=document.querySelector('[data-ease-back-to-top]');if(!(btn instanceof HTMLButtonElement))return;function update(){btn.hidden=window.scrollY<=300;}window.addEventListener('scroll',update,{passive:true});window.addEventListener('resize',update,{passive:true});btn.addEventListener('click',function(){window.scrollTo({top:0,behavior:'smooth'});});update();})();</script>`;

// ---------------------------------------------------------------------------
// Document assembly
// ---------------------------------------------------------------------------

export function renderToHtml(input: RenderInput): RenderedPage {
  // Funnels suppress navigation — override siteContext with empty nav
  const isFunnel = !!input.funnelContext;
  const effectiveInput = isFunnel && input.siteContext
    ? {
        ...input,
        siteContext: {
          ...input.siteContext,
          mainNav: [],
          footerNav: [],
        },
      }
    : input;

  const ctx = buildRenderContext(effectiveInput);
  // Wire recursive child renderer for zone-bearing blocks (PB-E).
  // Late-bound to avoid a circular reference with renderComponent's `ctx`.
  ctx.renderBlock = (child) => renderComponent(child, ctx);
  const siteCtx = effectiveInput.siteContext;
  const pageStateStyles = buildPublishedStateStyleTag(effectiveInput.puckData.content);
  const headerStateStyles = siteCtx?.headerContent
    ? buildPublishedStateStyleTag(extractComponents(siteCtx.headerContent))
    : "";
  const footerStateStyles = siteCtx?.footerContent
    ? buildPublishedStateStyleTag(extractComponents(siteCtx.footerContent))
    : "";
  const stateStyleTag = [pageStateStyles, headerStateStyles, footerStateStyles]
    .filter(Boolean)
    .join("\n");
  const head = buildHead(effectiveInput);
  const inlineScripts: string[] = [];

  const bodyParts: string[] = [];

  // Skip link — FIRST element in body
  bodyParts.push(
    '<a href="#main-content" class="ease-skip-link">Skip to content</a>',
  );

  if (input.settings.showScrollProgress === true) {
    bodyParts.push(
      '<div class="ease-scroll-progress" data-ease-scroll-progress aria-hidden="true" style="position:fixed;top:0;left:0;width:0%;height:3px;background:var(--ease-primary,var(--ease-color-brand,#1040CC));z-index:9999;pointer-events:none;transition:width 50ms linear;"></div>',
    );
    bodyParts.push(SCROLL_PROGRESS_SCRIPT);
    inlineScripts.push(SCROLL_PROGRESS_SCRIPT);
  }

  if (input.settings.showBackToTop === true) {
    bodyParts.push(
      '<button class="ease-back-to-top" data-ease-back-to-top aria-label="Back to top" type="button" hidden style="position:fixed;right:1rem;bottom:1rem;width:3rem;height:3rem;border:none;border-radius:999px;background:var(--ease-primary,var(--ease-color-brand,#1040CC));color:#fff;box-shadow:0 12px 32px rgba(15,23,42,.2);cursor:pointer;z-index:9999;transition:opacity 180ms ease,transform 180ms ease;">&#8593;</button>',
    );
    bodyParts.push(BACK_TO_TOP_SCRIPT);
    inlineScripts.push(BACK_TO_TOP_SCRIPT);
  }

  // Global header content (Puck JSON) — takes precedence over nav-based header
  const hasGlobalHeader = siteCtx?.headerContent && hasComponentContent(siteCtx.headerContent);

  if (hasGlobalHeader && siteCtx?.headerContent) {
    const headerComponents = extractComponents(siteCtx.headerContent);
    const headerHtml = headerComponents
      .map((component) => renderComponent(component, ctx))
      .join("\n");
    bodyParts.push(
      `<header class="ease-site-header ease-global-header" role="banner" style="view-transition-name: ease-site-header;">\n${headerHtml}\n</header>`,
    );
  } else {
    // Fall back to nav-based header
    const hasNavComponent = hasNavigationBarComponent(
      effectiveInput.puckData.content,
    );
    if (
      siteCtx &&
      siteCtx.mainNav.length > 0 &&
      !hasNavComponent
    ) {
      bodyParts.push(renderSiteHeader(siteCtx.mainNav));
    }
  }

  // Main content
  const hasAnimations = contentHasAnimation(effectiveInput.puckData.content)
    || contentHasAnimation(siteCtx?.headerContent ? extractComponents(siteCtx.headerContent) : undefined)
    || contentHasAnimation(siteCtx?.footerContent ? extractComponents(siteCtx.footerContent) : undefined);
  const hasLottie = hasLottieComponent(effectiveInput.puckData.content)
    || hasLottieComponent(siteCtx?.headerContent ? extractComponents(siteCtx.headerContent) : undefined)
    || hasLottieComponent(siteCtx?.footerContent ? extractComponents(siteCtx.footerContent) : undefined);
  const componentHtml = effectiveInput.puckData.content
    .map((component) => renderComponent(component, ctx))
    .join("\n");

  bodyParts.push(
    `<main id="main-content" tabindex="-1">\n${componentHtml}\n</main>`,
  );

  // Global footer content (Puck JSON) — takes precedence over nav-based footer
  const hasGlobalFooter = siteCtx?.footerContent && hasComponentContent(siteCtx.footerContent);

  if (hasGlobalFooter && siteCtx?.footerContent) {
    const footerComponents = extractComponents(siteCtx.footerContent);
    const footerHtml = footerComponents
      .map((component) => renderComponent(component, ctx))
      .join("\n");
    bodyParts.push(
      `<footer class="ease-site-footer ease-global-footer" role="contentinfo" style="view-transition-name: ease-site-footer;">\n${footerHtml}\n</footer>`,
    );
  } else if (siteCtx && siteCtx.footerNav.length > 0) {
    bodyParts.push(renderSiteFooter(siteCtx.footerNav));
  }

  // Body-end scripts: site first, then page (injected verbatim — intentional)
  // See head.ts for trust boundary documentation. CSP hashes cover these.
  if (effectiveInput.siteContext?.bodyEndScripts) {
    bodyParts.push(effectiveInput.siteContext.bodyEndScripts);
  }
  if (effectiveInput.settings.bodyEndScripts) {
    bodyParts.push(effectiveInput.settings.bodyEndScripts);
  }

  // Scroll animation observer — only when at least one component uses animations
  if (hasAnimations) {
    bodyParts.push(`<script>${ANIMATION_OBSERVER_SCRIPT}</script>`);
    inlineScripts.push(ANIMATION_OBSERVER_SCRIPT);
  }

  if (hasLottie) {
    bodyParts.push(`<script src="${LOTTIE_CDN_URL}"></script>`);
    bodyParts.push(LOTTIE_INIT_SCRIPT);
    inlineScripts.push(LOTTIE_INIT_SCRIPT);
  }

  // Funnel session tracking script — injected after other body-end scripts
  if (input.funnelContext) {
    const sessionScript = generateSessionScript(input.funnelContext.siteId);
    bodyParts.push(`<script>${sessionScript}</script>`);
    inlineScripts.push(sessionScript);
  }

  const analyticsScript = generatePageAnalyticsScript({
    tenantId: input.tenantId,
    pageId: input.pageId,
    versionId: input.versionId,
  });
  bodyParts.push(`<script>${analyticsScript}</script>`);
  inlineScripts.push(analyticsScript);

  const html = optimizeRenderedHtml(`<!DOCTYPE html>
<html lang="en">
<head>
${head}
${stateStyleTag}
</head>
<body>
${bodyParts.join("\n")}
</body>
</html>`);

  return {
    html,
    inlineScripts,
  };
}
